import { clsx } from 'clsx';
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

function Field({ id, label, hint, error, required, children }: {
  id: string; label?: string; hint?: string; error?: string; required?: boolean; children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-ink">
          {label} {required && <span className="text-red-500" aria-hidden>*</span>}
        </label>
      )}
      {children}
      {error
        ? <p role="alert" className="text-xs text-red-600 dark:text-red-400">{error}</p>
        : hint && <p className="text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string; hint?: string; error?: string; leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, className, leading, id, ...props }, ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Field id={fieldId} label={label} hint={hint} error={error} required={required}>
      <div className="relative">
        {leading && <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">{leading}</span>}
        <input
          ref={ref} id={fieldId} required={required} aria-invalid={!!error}
          className={clsx('input-base', leading && 'ps-9', error && 'border-red-500 focus:ring-red-500/30', className)}
          {...props}
        />
      </div>
    </Field>
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string; hint?: string; error?: string;
}>(function Select({ label, hint, error, required, className, id, children, ...props }, ref) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Field id={fieldId} label={label} hint={hint} error={error} required={required}>
      <select ref={ref} id={fieldId} required={required} className={clsx('input-base pe-8', className)} {...props}>
        {children}
      </select>
    </Field>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string; hint?: string; error?: string;
}>(function Textarea({ label, hint, error, required, className, id, ...props }, ref) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <Field id={fieldId} label={label} hint={hint} error={error} required={required}>
      <textarea ref={ref} id={fieldId} required={required} className={clsx('input-base min-h-[88px]', className)} {...props} />
    </Field>
  );
});
